/**
 * LayerDrawerProvider
 *
 * Context provider for managing Layer Drawer state and operations.
 * Integrates with LayerRegistry from Phase 2 and LayerService API.
 */


import React, { useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import { LayerRegistry, BaseLayer } from '../layers2';
import { LayerService } from '../../services/layerService';
import { Layer } from '../../types/layer';
import { LayerConfig } from '../layers2/BaseLayer';
import { addTrackLayer, removeTrackLayer, upsertTrackRow } from './utils/ephemeralTrackLayers';
import { LayerPreset } from '../../types/layerPreset';
import { DrawerHeight } from './utils/layerDrawerUtils';
import { initializeLayersInParallel } from './utils/initializeLayers';
import {
  loadPersistedLayerOpacities,
  persistLayerOpacity,
} from './utils/layerOpacityPersistence';
import { LayerDrawerContext, LayerDrawerContextType, LiveLayerDef } from './LayerDrawerContext';
// Register the LiveLayer providers actually rendered today: Weather (wind
// barbs), AFF (aircraft tracks), and Smokes.
import {
  getWeatherLayerProvider,
  getAffLayerProvider,
  getSmokeLayerProvider,
} from '../liveLayers/providers';
import { logger } from '../../utils/logger';

const log = logger('LayerDrawerProvider');

// LocalStorage key bases for persisting layer states. Real keys are scoped
// per user (e.g. em3-layer-states:<sub>) so shared workstations don't leak
// state between users. main.tsx writes em3:bootUserId before React mounts.
const LAYER_STATES_BASE = 'em3-layer-states';
const LIVE_LAYER_STATES_BASE = 'em3-live-layer-states';
// Active background's styleUrl, cached so EasyMap can pick it up sync on next
// load without waiting for permissions or the layers API. Avoids streets-v11
// flash when canReadLayers hasn't resolved yet.
const BG_URL_BASE = 'em3-bg-url';

function userScopedKey(base: string): string | null {
  const uid = sessionStorage.getItem('em3:bootUserId');
  return uid ? `${base}:${uid}` : null;
}

/**
 * Load persisted layer states from localStorage
 */
function loadPersistedLayerStates(): Record<string, boolean> {
  const key = userScopedKey(LAYER_STATES_BASE);
  if (!key) return {};
  try {
    const stored = localStorage.getItem(key);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (err) {
    log.warn('Failed to load persisted layer states:', err);
  }
  return {};
}

/**
 * Save layer states to localStorage. Also caches the active background's
 * styleUrl so EasyMap can render the right map on the next cold load
 * without an API roundtrip.
 */
function persistLayerStates(layers: Layer[]): void {
  const key = userScopedKey(LAYER_STATES_BASE);
  if (!key) return;
  try {
    const states: Record<string, boolean> = {};
    layers.forEach(layer => {
      states[layer.id] = layer.enabled;
    });
    localStorage.setItem(key, JSON.stringify(states));

    const bgUrlKey = userScopedKey(BG_URL_BASE);
    if (bgUrlKey) {
      const enabledBg = layers.find(l => l.category === 'backgrounds' && l.enabled);
      const url = enabledBg?.type === 'mapbox-style'
        ? (enabledBg.config?.styleUrl || enabledBg.source_url)
        : null;
      if (url) {
        localStorage.setItem(bgUrlKey, url);
      } else {
        localStorage.removeItem(bgUrlKey);
      }
    }
  } catch (err) {
    log.warn('Failed to persist layer states:', err);
  }
}

/**
 * Load persisted live layer states from localStorage
 */
function loadPersistedLiveLayerStates(): Record<string, boolean> {
  const key = userScopedKey(LIVE_LAYER_STATES_BASE);
  if (!key) return {};
  try {
    const stored = localStorage.getItem(key);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (err) {
    log.warn('Failed to load persisted live layer states:', err);
  }
  return {};
}

/**
 * Save live layer states to localStorage
 */
function persistLiveLayerStates(liveLayers: LiveLayerDef[]): void {
  const key = userScopedKey(LIVE_LAYER_STATES_BASE);
  if (!key) return;
  try {
    const states: Record<string, boolean> = {};
    liveLayers.forEach(layer => {
      states[layer.id] = layer.enabled;
    });
    localStorage.setItem(key, JSON.stringify(states));
  } catch (err) {
    log.warn('Failed to persist live layer states:', err);
  }
}

/**
 * LayerDrawerProvider Props
 */
interface LayerDrawerProviderProps {
  children: ReactNode;
}

/**
 * LayerDrawerProvider Component
 */
export const LayerDrawerProvider: React.FC<LayerDrawerProviderProps> = ({ children }) => {
  const [map, setMapState] = useState<mapboxgl.Map | null>(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [registry, setRegistry] = useState<LayerRegistry | null>(null);
  const [layers, setLayers] = useState<Layer[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [drawerHeight, setDrawerHeight] = useState<DrawerHeight>(DrawerHeight.HIDDEN);
  const [activeTab, setActiveTab] = useState<'active' | 'browse' | 'search'>('browse');
  const [searchTerm, setSearchTerm] = useState('');

  // Live layers (React-managed, not from database)
  const [liveLayers, setLiveLayers] = useState<LiveLayerDef[]>([]);
  // Store toggle callbacks for live layers
  const liveLayerCallbacksRef = useRef<Map<string, (enabled: boolean) => void>>(new Map());

  // Batch selection state
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedLayerIds, setSelectedLayerIds] = useState<Set<string>>(new Set());

  // Track if initial load has completed (use ref for synchronous access)
  const initialLoadCompleteRef = useRef(false);
  // Prevent concurrent refreshLayers calls
  const isRefreshingRef = useRef(false);
  // Track if we've enabled pending layers after map load (only do this once)
  const hasEnabledPendingLayersRef = useRef(false);
  // Ref for registry to avoid stale closure in callbacks
  const registryRef = useRef<LayerRegistry | null>(null);
  // Ref for mapLoaded to avoid stale closure
  const mapLoadedRef = useRef(false);

  const layerService = new LayerService();

  // Keep mapLoadedRef in sync with state
  useEffect(() => {
    mapLoadedRef.current = mapLoaded;
  }, [mapLoaded]);

  // Dev-only console helpers for testing layer-registry desync recovery.
  // Available on `window.__easymapDebug` only when `import.meta.env.DEV` is true.
  useEffect(() => {
    if (!import.meta.env.DEV || !registry) return;
    const debug = {
      listLayers: () => layers.map(l => ({ id: l.id, name: l.name, category: l.category })),
      /** Remove a layer from the registry only — leaves React state intact.
       *  After calling, click the layer's toggle to test the recovery path
       *  in toggleLayer (silently rebuilds) or the visible refresh banner. */
      desyncLayer: async (layerId: string) => {
        if (!registry.has(layerId)) {
          console.warn(`[easymapDebug] Layer ${layerId} not in registry`);
          return;
        }
        await registry.remove(layerId);
        console.log(`[easymapDebug] Removed ${layerId} from registry. State intact. Click the toggle to test recovery.`);
      },
    };
    (window as unknown as { __easymapDebug?: unknown }).__easymapDebug = debug;
    return () => {
      delete (window as unknown as { __easymapDebug?: unknown }).__easymapDebug;
    };
  }, [registry, layers]);

  // Reset LayerRegistry on unmount (handles React StrictMode remounting)
  useEffect(() => {
    return () => {
      log.debug('Unmounting - resetting LayerRegistry');
      LayerRegistry.resetInstance();
    };
  }, []);

  /**
   * Set map instance and initialize registry
   */
  const setMap = useCallback((mapInstance: mapboxgl.Map) => {
    setMapState(mapInstance);
    const registryInstance = LayerRegistry.getInstance(mapInstance);

    // Register the LiveLayer providers under stable IDs so DB rows of
    // type='live' with `config.providerId` matching one of these resolve
    // to the right provider when createLayer() runs. Keeps Live Weather,
    // AFF, and Fire Hotspots edit-able in the layers admin like every
    // other layer instead of being hardcoded React mounts in EasyMap.tsx.
    registryInstance.registerProvider('weather-stations', getWeatherLayerProvider());
    registryInstance.registerProvider('aff', getAffLayerProvider());
    registryInstance.registerProvider('smokes', getSmokeLayerProvider());

    setRegistry(registryInstance);

    if (mapInstance.loaded()) {
      setMapLoaded(true);
    } else {
      mapInstance.on('load', () => {
        log.debug('Map ready');
        setMapLoaded(true);
      });
    }
  }, []);

  /**
   * Enable layers that should be enabled after map loads
   *
   * This handles the case where refreshLayers() was called before the map was loaded.
   * Those layers were created in the registry but couldn't be enabled on the map.
   * Once mapLoaded becomes true, this effect enables them.
   *
   * Uses hasEnabledPendingLayersRef to ensure this only runs once.
   */
  useEffect(() => {
    log.debug(`Enable pending check: mapLoaded=${mapLoaded}, registry=${!!registry}, layers=${layers.length}`);

    // Only run once when map becomes loaded after initial load is complete
    // Also wait for layers array to be populated (not empty)
    if (!mapLoaded || !registry || !initialLoadCompleteRef.current || hasEnabledPendingLayersRef.current || layers.length === 0) {
      return;
    }


    // Check if there are actually layers waiting to be enabled
    const pendingLayers = layers.filter(layer => {
      if (!layer.enabled) return false;
      const instance = registry.get(layer.id);
      const instanceEnabled = instance?.isEnabled() || false;
      return instance && !instanceEnabled;
    });

    if (pendingLayers.length === 0) {
      hasEnabledPendingLayersRef.current = true;
      return;
    }

    log.debug(`Enabling ${pendingLayers.length} pending layers:`, pendingLayers.map(l => l.name));
    hasEnabledPendingLayersRef.current = true;

    // Enable layers that are marked as enabled in state but not yet enabled on the map
    const enablePendingLayers = async () => {
      let enabledCount = 0;
      for (const layer of pendingLayers) {
        const instance = registry.get(layer.id);
        if (instance && !instance.isEnabled()) {
          try {
            await instance.enable();
            enabledCount++;
          } catch (err) {
            log.error(`Failed to enable ${layer.name}:`, err);
          }
        }
      }

      if (enabledCount > 0) {
        // Reorder all layers after enabling to ensure correct z-order (backgrounds < overlays)
        registry.reorderAllLayers();
      }
    };

    enablePendingLayers();
  }, [mapLoaded, registry, layers]);

  /**
   * Fetch all layers from API
   */
  const refreshLayers = useCallback(async () => {
    // Use refs to get current values (avoid stale closure)
    const currentRegistry = registryRef.current;
    const currentMapLoaded = mapLoadedRef.current;

    // Prevent concurrent refresh calls (race condition fix)
    if (isRefreshingRef.current) return;
    if (!currentRegistry) return;

    isRefreshingRef.current = true;
    log.debug(`refreshLayers (mapLoaded: ${currentMapLoaded})`);

    setLoading(true);
    setError(null);

    try {
      // Load persisted states from localStorage (for page refresh scenarios)
      const persistedStates = loadPersistedLayerStates();
      const hasPersistedStates = Object.keys(persistedStates).length > 0;

      // Only capture runtime states AFTER initial load is complete
      // This prevents the race condition where newly-created (but not yet enabled) layers
      // override the persisted states with "false"
      const runtimeStates: Record<string, boolean> = {};
      if (initialLoadCompleteRef.current && currentRegistry) {
        const registryLayerIds = currentRegistry.getAllIds();
        for (const layerId of registryLayerIds) {
          const instance = currentRegistry.get(layerId);
          if (instance) {
            runtimeStates[layerId] = instance.isEnabled();
          }
        }
      }

      const fetchedLayers = await layerService.getAllLayers();

      // Remember database default background BEFORE applying localStorage
      // This is the fallback when user has all backgrounds disabled
      const backgroundLayers = fetchedLayers.filter(l => l.category === 'backgrounds');
      const dbDefaultBackground = backgroundLayers.find(l => l.enabled);

      // Apply states in priority order:
      // 1. Runtime state (from current session, only after initial load)
      // 2. Persisted state (from localStorage) - user preference wins
      // 3. Database default
      fetchedLayers.forEach(layer => {
        if (initialLoadCompleteRef.current && runtimeStates[layer.id] !== undefined) {
          layer.enabled = runtimeStates[layer.id];
        } else if (hasPersistedStates && persistedStates[layer.id] !== undefined) {
          layer.enabled = persistedStates[layer.id];
        }
      });

      // Ensure only one background layer is enabled
      const enabledBackgrounds = backgroundLayers.filter(l => l.enabled);

      if (enabledBackgrounds.length > 1) {
        // Multiple backgrounds enabled - only keep the first one
        fetchedLayers.forEach(layer => {
          if (layer.category === 'backgrounds' && layer.id !== enabledBackgrounds[0].id) {
            layer.enabled = false;
          }
        });
      } else if (enabledBackgrounds.length === 0 && backgroundLayers.length > 0) {
        // No backgrounds enabled - use database default, or first available as last resort
        const fallbackBg = dbDefaultBackground || backgroundLayers[0];
        fallbackBg.enabled = true;
      }

      // Clean up layers in registry that no longer exist in the database
      const fetchedLayerIds = new Set(fetchedLayers.map(l => l.id));
      const registryLayerIds = currentRegistry.getAllIds();

      for (const layerId of registryLayerIds) {
        if (!fetchedLayerIds.has(layerId)) {
          try {
            await currentRegistry.remove(layerId);
          } catch (err) {
            log.error(`Failed to remove layer ${layerId}:`, err);
          }
        }
      }

      // Restore the user's persisted per-layer opacity (#625) onto the fetched
      // configs BEFORE initialization, so each layer initializes its
      // currentOpacity from the user's value rather than the database default.
      const savedOpacities = loadPersistedLayerOpacities();
      fetchedLayers.forEach(layer => {
        const saved = savedOpacities[layer.id];
        if (typeof saved === 'number') {
          layer.default_opacity = saved;
        }
      });

      // Initialize/update layers in registry — parallel with per-layer
      // timeout so a hung WMS source can't block the rest of the map.
      await initializeLayersInParallel(fetchedLayers, currentRegistry, {
        mapLoaded: currentMapLoaded,
      });

      // Reorder all layers after enabling to ensure correct z-ordering
      currentRegistry.reorderAllLayers();

      // Sync the layers state with registry
      // IMPORTANT: If map isn't loaded yet, keep the INTENDED enabled state (from localStorage/DB)
      // so the "enable pending layers" effect can enable them later.
      // Only sync to actual isEnabled() state if map is loaded (meaning enable was attempted).
      const syncedLayers = fetchedLayers.map(layer => {
        const instance = currentRegistry.get(layer.id);
        if (instance) {
          // If map is loaded, sync to actual state. If not, keep intended state.
          const actuallyEnabled = currentMapLoaded ? instance.isEnabled() : layer.enabled;
          return {
            ...layer,
            enabled: actuallyEnabled
          };
        }
        return layer;
      });

      setLayers(syncedLayers);
      persistLayerStates(syncedLayers);
      if (!initialLoadCompleteRef.current) {
        initialLoadCompleteRef.current = true;
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load layers';
      setError(errorMessage);
      log.error('Error loading layers:', err);
    } finally {
      setLoading(false);
      isRefreshingRef.current = false;
    }
  }, []); // No dependencies - uses refs for current values

  /**
   * Toggle layer on/off
   * Background layers are mutually exclusive (radio button behavior)
   * Overlay layers can stack (checkbox behavior)
   */
  const toggleLayer = useCallback(async (layerId: string) => {
    if (!registry) {
      throw new Error('Registry not initialized');
    }

    try {
      // Get the layer being toggled
      const targetLayer = layers.find(l => l.id === layerId);
      let targetInstance = registry.get(layerId);

      // Recovery path: layer is in state but missing from registry — usually a
      // race during initial load (e.g., createLayer threw before the map was
      // fully ready). Attempt to rebuild the registry entry on demand before
      // giving up so the user doesn't have to refresh.
      if (targetLayer && !targetInstance) {
        log.warn(`Layer ${layerId} (${targetLayer.name}) missing from registry — attempting recovery`);
        try {
          const instance = registry.createLayer(targetLayer);
          registry.add(instance);
          await instance.initialize();
          targetInstance = instance;
          log.success(`Layer ${targetLayer.name} recovered`);
        } catch (recoveryErr) {
          log.error(`Recovery failed for ${targetLayer.name}:`, recoveryErr);
          throw new Error(
            `Layer "${targetLayer.name}" failed to load. Please refresh the page.`
          );
        }
      }

      if (!targetLayer || !targetInstance) {
        // Log diagnostic info to help debug layer loading issues
        log.error(`Layer ${layerId} not found`, {
          inState: !!targetLayer,
          inRegistry: !!targetInstance,
          totalLayers: layers.length,
          totalRegistryLayers: registry.getAllIds().length,
        });
        throw new Error(
          targetLayer
            ? `Layer "${targetLayer.name}" failed to load. Please refresh the page.`
            : `Layer ${layerId} not found. Please refresh the page.`
        );
      }

      // Check if this is a background layer being enabled
      const isBackgroundLayer = targetLayer.category === 'backgrounds';
      const willBeEnabled = !targetInstance.isEnabled();

      // If enabling a background layer, disable all other background layers first
      if (isBackgroundLayer && willBeEnabled) {
        const otherBackgroundLayers = layers.filter(
          l => l.category === 'backgrounds' && l.id !== layerId
        );

        // Disable all other background layers
        for (const layer of otherBackgroundLayers) {
          const instance = registry.get(layer.id);
          if (instance && instance.isEnabled()) {
            await registry.disableLayer(layer.id);
          }
        }

        // Update local state for disabled backgrounds
        setLayers(prev => {
          const updated = prev.map(layer => {
            if (layer.category === 'backgrounds' && layer.id !== layerId) {
              return { ...layer, enabled: false };
            }
            return layer;
          });

          // Persist updated states
          persistLayerStates(updated);

          return updated;
        });
      }

      // Now toggle the target layer
      await registry.toggleLayer(layerId);

      // Reorder all layers to ensure proper category-based stacking
      // (backgrounds < overlays < live)
      registry.reorderAllLayers();

      // Update local state for the toggled layer
      setLayers(prev => {
        const updated = prev.map(layer => {
          if (layer.id === layerId) {
            const instance = registry.get(layerId);
            return {
              ...layer,
              enabled: instance?.isEnabled() || false
            };
          }
          return layer;
        });

        // Persist updated states to localStorage
        persistLayerStates(updated);

        return updated;
      });
    } catch (err) {
      log.error(`Failed to toggle layer ${layerId}:`, err);
      throw err;
    }
  }, [registry, layers]);

  /**
   * Set layer opacity
   */
  const setLayerOpacity = useCallback(async (layerId: string, opacity: number) => {
    if (!registry) {
      throw new Error('Registry not initialized');
    }

    try {
      await registry.setLayerOpacity(layerId, opacity);

      // Persist so the user's opacity survives a refresh (#625). refreshLayers
      // otherwise re-initializes from the database default_opacity.
      persistLayerOpacity(layerId, opacity);

      // Update local state
      setLayers(prev => prev.map(layer => {
        if (layer.id === layerId) {
          return {
            ...layer,
            default_opacity: opacity
          };
        }
        return layer;
      }));
    } catch (err) {
      log.error(`Failed to set opacity for layer ${layerId}:`, err);
      throw err;
    }
  }, [registry]);

  /**
   * Reorder layers
   */
  const reorderLayers = useCallback(async (layerIds: string[]) => {
    if (!registry) {
      throw new Error('Registry not initialized');
    }

    try {
      // Update order in registry
      const orders = layerIds.map((id, index) => ({
        id,
        order: layerIds.length - index // Higher index = higher order (on top)
      }));

      orders.forEach(({ id, order }) => {
        registry.setLayerOrder(id, order);
      });

      // Reorder on map
      registry.reorderAllLayers();

      // Update backend
      await layerService.updateLayerOrders(orders);

      // Update local state
      setLayers(prev => prev.map(layer => {
        const orderData = orders.find(o => o.id === layer.id);
        if (orderData) {
          return {
            ...layer,
            default_order: orderData.order
          };
        }
        return layer;
      }));
    } catch (err) {
      log.error('Failed to reorder layers:', err);
      throw err;
    }
  }, [registry]);

  /**
   * Get layer instance from registry
   */
  const getLayerInstance = useCallback((layerId: string): BaseLayer | undefined => {
    return registry?.get(layerId);
  }, [registry]);

  /**
   * Get enabled layers
   */
  const getEnabledLayers = useCallback((): Layer[] => {
    return layers.filter(layer => layer.enabled);
  }, [layers]);

  /**
   * Load a preset - apply saved layer configuration
   */
  const loadPreset = useCallback(async (preset: LayerPreset): Promise<void> => {
    if (!registry) {
      throw new Error('Registry not initialized');
    }

    const { preset_data } = preset;

    try {
      // Step 1: Disable all currently enabled layers (except backgrounds for now)
      for (const layer of layers) {
        if (layer.enabled && layer.category !== 'backgrounds') {
          const instance = registry.get(layer.id);
          if (instance && instance.isEnabled()) {
            await registry.disableLayer(layer.id);
          }
        }
      }

      // Step 2: Disable all live layers
      for (const liveLayer of liveLayers) {
        if (liveLayer.enabled) {
          const callback = liveLayerCallbacksRef.current.get(liveLayer.id);
          if (callback) {
            callback(false);
          }
        }
      }

      // Step 3: Enable layers from the preset with their saved opacity and order
      for (const presetLayer of preset_data.layers) {
        const layer = layers.find(l => l.id === presetLayer.id);
        if (!layer) {
          log.warn(`Preset layer ${presetLayer.id} not found, skipping`);
          continue;
        }

        // Handle background layer switching
        if (layer.category === 'backgrounds') {
          // Disable other backgrounds first
          for (const otherLayer of layers) {
            if (otherLayer.category === 'backgrounds' && otherLayer.id !== layer.id) {
              const instance = registry.get(otherLayer.id);
              if (instance && instance.isEnabled()) {
                await registry.disableLayer(otherLayer.id);
              }
            }
          }
        }

        // Enable the layer
        const instance = registry.get(presetLayer.id);
        if (instance) {
          if (!instance.isEnabled()) {
            await instance.enable();
          }
          // Set opacity
          await instance.setOpacity(presetLayer.opacity);
          // Set order
          instance.setOrder(presetLayer.order);
        }
      }

      // Step 4: Handle background layer from preset if specified
      if (preset_data.backgroundLayerId) {
        const bgLayer = layers.find(l => l.id === preset_data.backgroundLayerId);
        if (bgLayer && bgLayer.category === 'backgrounds') {
          // Disable other backgrounds
          for (const otherLayer of layers) {
            if (otherLayer.category === 'backgrounds' && otherLayer.id !== bgLayer.id) {
              const instance = registry.get(otherLayer.id);
              if (instance && instance.isEnabled()) {
                await registry.disableLayer(otherLayer.id);
              }
            }
          }
          // Enable preset background
          const bgInstance = registry.get(bgLayer.id);
          if (bgInstance && !bgInstance.isEnabled()) {
            await bgInstance.enable();
          }
        }
      }

      // Step 5: Enable live layers from preset
      for (const presetLiveLayer of preset_data.liveLayers || []) {
        if (presetLiveLayer.enabled) {
          const callback = liveLayerCallbacksRef.current.get(presetLiveLayer.id);
          if (callback) {
            callback(true);
          }
        }
      }

      // Step 6: Reorder all layers
      registry.reorderAllLayers();

      // Step 7: Update local state to reflect new enabled/opacity/order values
      setLayers(prev => {
        const updated = prev.map(layer => {
          const presetLayer = preset_data.layers.find(pl => pl.id === layer.id);
          const instance = registry.get(layer.id);

          if (presetLayer) {
            return {
              ...layer,
              enabled: instance?.isEnabled() || false,
              default_opacity: presetLayer.opacity,
              default_order: presetLayer.order,
            };
          }

          // Not in preset - check if it's the background
          if (layer.id === preset_data.backgroundLayerId) {
            return {
              ...layer,
              enabled: instance?.isEnabled() || false,
            };
          }

          // Not in preset and not background - mark as disabled
          return {
            ...layer,
            enabled: instance?.isEnabled() || false,
          };
        });

        // Persist to localStorage
        persistLayerStates(updated);

        return updated;
      });

      // Step 8: Update live layer states
      setLiveLayers(prev => {
        const updated = prev.map(ll => {
          const presetLive = preset_data.liveLayers?.find(pl => pl.id === ll.id);
          return {
            ...ll,
            enabled: presetLive?.enabled || false,
          };
        });

        persistLiveLayerStates(updated);

        return updated;
      });

      // Step 9: Restore viewport (#557). Saved maps capture center/zoom too;
      // older presets without a viewport leave the camera where it is.
      if (preset_data.viewport && map) {
        const { lng, lat, zoom } = preset_data.viewport;
        map.flyTo({ center: [lng, lat], zoom });
      }

    } catch (err) {
      log.error('Failed to load preset:', err);
      throw err;
    }
  }, [registry, layers, liveLayers, map]);

  /**
   * Toggle selection mode on/off
   * Clears selection when exiting selection mode
   */
  const toggleSelectionMode = useCallback(() => {
    setSelectionMode(prev => {
      if (prev) {
        // Exiting selection mode - clear selection
        setSelectedLayerIds(new Set());
      }
      return !prev;
    });
  }, []);

  /**
   * Toggle layer selection
   */
  const toggleLayerSelection = useCallback((layerId: string) => {
    setSelectedLayerIds(prev => {
      const next = new Set(prev);
      if (next.has(layerId)) {
        next.delete(layerId);
      } else {
        next.add(layerId);
      }
      return next;
    });
  }, []);

  /**
   * Select all currently-active layers (overlays + live layers that are
   * enabled, not backgrounds). Selection mode is only entered from the
   * Active tab, so "All" should mean "everything visible in this tab" —
   * including all 29 catalogued layers (most of which aren't on the map)
   * would make the Enable/Disable batch buttons destructive.
   */
  const selectAllLayers = useCallback(() => {
    const overlayIds = layers
      .filter(l => l.enabled && l.category !== 'backgrounds')
      .map(l => l.id);
    const liveLayerIds = liveLayers
      .filter(l => l.enabled)
      .map(l => l.id);
    setSelectedLayerIds(new Set([...overlayIds, ...liveLayerIds]));
  }, [layers, liveLayers]);

  /**
   * Deselect all layers
   */
  const deselectAllLayers = useCallback(() => {
    setSelectedLayerIds(new Set());
  }, []);

  /**
   * Enable all selected layers (including live layers)
   */
  const enableSelectedLayers = useCallback(async () => {
    if (!registry) return;

    for (const layerId of selectedLayerIds) {
      // Check if it's a database layer
      const layer = layers.find(l => l.id === layerId);
      if (layer && !layer.enabled) {
        try {
          await toggleLayer(layerId);
        } catch (err) {
          log.error(`Failed to enable layer ${layerId}:`, err);
        }
        continue;
      }

      // Check if it's a live layer
      const liveLayer = liveLayers.find(l => l.id === layerId);
      if (liveLayer && !liveLayer.enabled) {
        const callback = liveLayerCallbacksRef.current.get(layerId);
        if (callback) {
          callback(true);
        }
      }
    }
  }, [registry, selectedLayerIds, layers, liveLayers, toggleLayer]);

  /**
   * Disable all selected layers (including live layers)
   */
  const disableSelectedLayers = useCallback(async () => {
    if (!registry) return;

    for (const layerId of selectedLayerIds) {
      // Check if it's a database layer
      const layer = layers.find(l => l.id === layerId);
      if (layer && layer.enabled) {
        try {
          await toggleLayer(layerId);
        } catch (err) {
          log.error(`Failed to disable layer ${layerId}:`, err);
        }
        continue;
      }

      // Check if it's a live layer
      const liveLayer = liveLayers.find(l => l.id === layerId);
      if (liveLayer && liveLayer.enabled) {
        const callback = liveLayerCallbacksRef.current.get(layerId);
        if (callback) {
          callback(false);
        }
      }
    }
  }, [registry, selectedLayerIds, layers, liveLayers, toggleLayer]);

  /**
   * Register a live layer (React-managed, not from database)
   * Checks localStorage for persisted state and applies it
   */
  const registerLiveLayer = useCallback((layer: LiveLayerDef, onToggle: (enabled: boolean) => void) => {
    liveLayerCallbacksRef.current.set(layer.id, onToggle);

    // Check for persisted state
    const persistedStates = loadPersistedLiveLayerStates();
    const persistedEnabled = persistedStates[layer.id];

    // If we have a persisted state that differs from current, apply it
    if (persistedEnabled !== undefined && persistedEnabled !== layer.enabled) {
      onToggle(persistedEnabled);
      layer = { ...layer, enabled: persistedEnabled };
    }

    setLiveLayers(prev => {
      // Don't add duplicates
      if (prev.some(l => l.id === layer.id)) {
        return prev.map(l => l.id === layer.id ? layer : l);
      }
      return [...prev, layer];
    });
  }, []);

  /**
   * Unregister a live layer
   */
  const unregisterLiveLayer = useCallback((layerId: string) => {
    liveLayerCallbacksRef.current.delete(layerId);
    setLiveLayers(prev => prev.filter(l => l.id !== layerId));
  }, []);

  /**
   * Register a drawn AFF track as an ephemeral registry layer (#802).
   *
   * Tracks used to be drawn straight onto the map, so the drawer never knew
   * they existed and none of its controls reached them. Going through the
   * registry is what gives a track visibility, opacity and z-order — and z-order
   * in particular only works from here, because reorderAllLayers restacks every
   * registry layer and would otherwise leave tracks wherever mapbox left them.
   */
  const registerTrackLayer = useCallback(async (config: LayerConfig) => {
    if (!registry || !map) {
      throw new Error('Cannot register a track layer before the map and registry are ready');
    }

    const row = await addTrackLayer(registry, map, config);

    setLayers(prev => upsertTrackRow(prev, row));
  }, [registry, map]);

  const unregisterTrackLayer = useCallback(async (layerId: string) => {
    if (registry) {
      await removeTrackLayer(registry, layerId);
    }
    setLayers(prev => prev.filter(l => l.id !== layerId));
  }, [registry]);

  /**
   * Toggle a live layer on/off
   */
  const toggleLiveLayer = useCallback((layerId: string) => {
    const callback = liveLayerCallbacksRef.current.get(layerId);
    const layer = liveLayers.find(l => l.id === layerId);

    if (callback && layer) {
      const newEnabled = !layer.enabled;
      callback(newEnabled);
      setLiveLayers(prev => {
        const updated = prev.map(l =>
          l.id === layerId ? { ...l, enabled: newEnabled } : l
        );
        persistLiveLayerStates(updated);
        return updated;
      });
    }
  }, [liveLayers]);

  /**
   * Update live layer enabled state (called by EasyMap when state changes)
   * Also persists the state to localStorage
   */
  const updateLiveLayerState = useCallback((layerId: string, enabled: boolean) => {
    setLiveLayers(prev => {
      const updated = prev.map(l =>
        l.id === layerId ? { ...l, enabled } : l
      );
      // Persist to localStorage
      persistLiveLayerStates(updated);
      return updated;
    });
  }, []);

  /**
   * Auto-load layers when registry is initialized
   *
   * This replaces the call from EasyMap's LayerDrawerWrapper which was causing
   * a race condition (refreshLayers was called before registry state was set).
   * Now we wait until registry is actually set before calling refreshLayers.
   */
  useEffect(() => {
    // Update ref synchronously before using it
    registryRef.current = registry;

    if (registry && !initialLoadCompleteRef.current && !isRefreshingRef.current) {
      refreshLayers().catch(err => {
        log.error('Load failed:', err.message);
      });
    }
  }, [registry, refreshLayers]);

  const value: LayerDrawerContextType = {
    map,
    registry,
    layers,
    loading,
    error,
    drawerHeight,
    activeTab,
    searchTerm,
    // Live layer management
    liveLayers,
    registerLiveLayer,
    unregisterLiveLayer,
    registerTrackLayer,
    unregisterTrackLayer,
    toggleLiveLayer,
    updateLiveLayerState,
    // Actions
    setMap,
    refreshLayers,
    toggleLayer,
    setLayerOpacity,
    reorderLayers,
    setDrawerHeight,
    setActiveTab,
    setSearchTerm,
    getLayerInstance,
    getEnabledLayers,
    // Preset management
    loadPreset,
    // Batch selection
    selectionMode,
    selectedLayerIds,
    toggleSelectionMode,
    toggleLayerSelection,
    selectAllLayers,
    deselectAllLayers,
    enableSelectedLayers,
    disableSelectedLayers
  };

  return (
    <LayerDrawerContext.Provider value={value}>
      {children}
    </LayerDrawerContext.Provider>
  );
};
